import { MouseEvent, useRef, useEffect, useState } from 'react';
import { IconFavorite, IconInfo, IconDemo, IconMoreVert, IconFavoriteBorder, IconOpenInNew, IconSettings } from 'cdk-radial';
import * as Styles from './styles';
import toTestId from '../../testUtils/toTestId';
import { useTranslation } from 'react-i18next';
import { useShowDialog } from '../../contexts/AppStateContext';
import launchExternal from '../../util/launchExternal';
import { isBlockedByOS, shouldShowWarning } from '../../util/osUtils';
import { canOpenDrive } from '../../util/DriveUtils';
import { withTooltip, THEMES } from 'cdk-radial';
import { useAdoptEaseStore, useUserLocaleStore } from '../../Store/index'
import AdoptEaseApplicationDialog from '../AdoptEase/AdoptEaseApplicationDialog';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk'
import { useUpdateUserAdoptEaseUsage } from '../../hooks/queries';
import { INeverUsedApp } from '../../types';

interface Props {
  isEnabled: boolean;
  isFavorite?: boolean;
  description: string;
  name: string;
  iconText: string;
  onOpenClick: (e: MouseEvent) => void;
  onFavoriteClick: () => void;
  iconColor: string;
  isIconRequired: boolean;
  showFavoriteIcon?: boolean;
  tabIndex?: number;
  cardHieght?: number;
  showDescription?: boolean;
  onCdkuOpen?: Function;
  cdkuUrl?: string;
  menu?: CardMenu[];
  menuHandler?: Function;
  isMobileApp?: boolean;
  isRestricted?: boolean;
  restrictedTooltipMsg?: string;
  url?: string;
  openInNewTab?: boolean;
  id?: string;
  dmsCheck?: Function,
  isDriveApp?: boolean
  warningMessage?: string;
  requiredOS?: string[];
  showNewIndicator?: boolean;
  isDefault?: boolean;
  showStartupOption?: boolean;
  setDefaultApp: () => void;
}

export interface CardMenu {
  key: string;
  label: string;
  icon?: any;
}

const ComponentToRender = (props: any) => {
  const { t } = useTranslation();
  return (
    <Styles.WarningMessage {...props}>
      <Styles.WarningIcon height={18} width={18} dataTestId="restricted-warning" />
      {t('baseCard.restrictedWarning')}
    </Styles.WarningMessage>
  );
};

export const RestrictedTooltip = ({ message }: any) => {
  const RestrictedWarningIcon = withTooltip(ComponentToRender,
    {
      id: `restricted-tooltip-icon`,
      text: message,
      theme: THEMES.LIGHT,
      styles: { zIndex: 1002, display: window.innerWidth < 501 && 'none' }
    }
  );
  return <RestrictedWarningIcon></RestrictedWarningIcon>;
}

export default function BaseCard({
  isFavorite,
  isEnabled,
  description,
  name,
  iconText,
  iconColor,
  onOpenClick,
  onFavoriteClick,
  isIconRequired,
  showFavoriteIcon = true,
  tabIndex = 0,
  cardHieght = 80,
  showDescription = false,
  onCdkuOpen,
  cdkuUrl,
  menu,
  menuHandler,
  isMobileApp = false,
  isRestricted = false,
  restrictedTooltipMsg,
  url,
  openInNewTab = false,
  id,
  dmsCheck,
  isDriveApp = false,
  warningMessage,
  requiredOS,
  showNewIndicator,
  isDefault = false,
  showStartupOption = false,
  setDefaultApp
}: Props) {
  const { t } = useTranslation();
  const Icon = isFavorite ? IconFavorite : IconFavoriteBorder;
  const { trackEvent } = useAnalytics();
  /*** Card Menu */

  const moreRef = useRef<HTMLElement | null>(null);
  const showDialog = useShowDialog();
  const osBlocked = isBlockedByOS(requiredOS);

  const getLaunchType = () => {
    const urlParams = new URLSearchParams(url?.split('?')[1]);

    return urlParams.get('launchtype');
  }

  // cloned so the pushes below never mutate a caller-supplied menu array
  const menuOptions: CardMenu[] = menu ? [...menu] : [
    { label: isFavorite ? t('baseCard.menu.removeFromMyUnify') : t('baseCard.menu.addToMyUnify'), key: 'MY_UNIFY', icon: <Icon /> },
    { label: t('baseCard.menu.learnMore'), key: 'LEARN_MORE', icon: <IconInfo /> },
  ]
  cdkuUrl && menuOptions.push({ label: t('baseCard.menu.cdku'), key: 'CDKU', icon: <IconDemo /> });
  openInNewTab && !osBlocked && menuOptions.push({ label: t('baseCard.menu.openNewTab'), key: 'OPEN_IN_TAB', icon: <IconOpenInNew /> });
  isDriveApp && canOpenDrive() && menuOptions.push({ label: "Adaptiva OSD", key: 'OPEN_DMS', icon: <IconOpenInNew /> });

  showStartupOption && !osBlocked && getLaunchType() !== 'tab' && menuOptions.push({
    label: isDefault
      ? t('baseCard.startupApp.removeLabel')
      : t('baseCard.startupApp.setLabel'),
    key: 'DEFAULT_APP',
    icon: <IconSettings />
  });

  const { userLocale } = useUserLocaleStore();

  const onMoreClick = (event: MouseEvent) => {
    event.stopPropagation();
    return false;
  }

  const openTileInNewTab = (id: string, url: string) => {
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const launchType = urlParams.get('launchtype');
    if (launchType === 'tab') {
      const thisEl = document.getElementById(`wf-card-${toTestId(name)}`);
      thisEl?.click();
      return;
    }

    const dpId = (id.substring(0, 1) === 'F' && id !== 'FORTELLIS') ? `dp_wf_${id}` : `dp_ap_${id}`;
    window.open(`${window.location.protocol}//${window.location.host}/${dpId}/${url}`, '_blank');
    setIsApplicationopened(true);
    setEnableAdoptease(false);
  };

  const openDmsLink = (link: string) => {
    window.open(`${link}`, '_blank');
  }

  const onMenuClick = (option: CardMenu) => {
    if (menuHandler) return menuHandler(option);
    switch (option.key) {
      case 'MY_UNIFY':
        onFavoriteClick();
        break;

      case 'LEARN_MORE':
        showDialog('LEARN_MORE', {
          iconColor,
          iconText,
          name,
          description,
          isEnabled,
          showDescription: true,
        });
        break;

      case 'CDKU':
        launchExternal(cdkuUrl as string) || (onCdkuOpen && onCdkuOpen(cdkuUrl));
        break;

      case 'DEFAULT_APP':
        setDefaultApp();
        break;

      case 'OPEN_IN_TAB':
        if (id && url) {
          trackEvent(AMPLITUDE_EVENTS.OPEN_IN_NEW_TAB, {
            'Application': name
          });
          openTileInNewTab(id, url);
        }
        break;
      case 'OPEN_DMS':
        dmsCheck?.(true).then((result: any) => {
          if (typeof result === "string") { openDmsLink(result) }
        });
        break;
    }
  }

  // Radial menu click is causing card click by default, so ignoring menu click
  let clickByMenu = false;
  const onCardClick = (e: MouseEvent) => {
    !clickByMenu && !isMobileApp && onOpenClick(e);
    if (!clickByMenu && !isMobileApp) {
      setIsApplicationopened(true);
      setEnableAdoptease(false);
    }
    clickByMenu = false;
  }

  const [enableAdoptease, setEnableAdoptease] = useState(false);
  const [showAdopteaseDialogue, setShowAdopteaseDialogue] = useState(false);
  const neverUsedApps: INeverUsedApp[] = useAdoptEaseStore((state: any) => state.neverUsedApps);
  const setEnableAdopteaseApps = useAdoptEaseStore((state: any) => state.setEnableAdopteaseApps);
  const adoptEaseISFlag = useFeatureFlag('UNIFY_UI_ADOPTEASE_APPLICATION');
  const [isApplicationopened, setIsApplicationopened] = useState(JSON.parse(localStorage.getItem('opened-apps') || '[]').includes(id));

  useEffect(() => {
    if (neverUsedApps.filter(app => app.appId === id).length > 0 && !isApplicationopened) {
      trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_TRYNOW_SEEN, {
        'Application': name
      });
      setEnableAdoptease(true);
    }
    else {
      setEnableAdoptease(false);
    }
  }, [setEnableAdoptease, id, name, neverUsedApps, trackEvent, isApplicationopened]);

  const { mutate: mutateAdoptEaseUpdate } = useUpdateUserAdoptEaseUsage();
  const updateAdoptEaseUsage = () => {
    const now = (new Date());
    const record: Record<string, Date> = { [name]: now };
    mutateAdoptEaseUpdate(record);
  };

  const tryNowClick = (event: MouseEvent) => {
    updateAdoptEaseUsage();
    setEnableAdoptease(false);
    setEnableAdopteaseApps(false);
    setShowAdopteaseDialogue(true)
    event.stopPropagation();
    trackEvent(AMPLITUDE_EVENTS.ADOPTEASE_APPLICATION_MODAL_SEEN, {
      'Application': name
    });
    return false;
  }

  return (
    <>
      <Styles.Card
        data-testid={`card-${toTestId(name)}`}
        id={`wf-card-${toTestId(name)}`}
        onClick={(e) => !isRestricted && isEnabled && !osBlocked && onCardClick(e)}
        tabIndex={tabIndex}
        onKeyDown={(e: any) => {
          if (e.key === 'Enter') {
            !isRestricted && isEnabled && !osBlocked && onCardClick(e)
          }
        }}
      >
        <div>
          <Styles.CardHeader>
            {isIconRequired && (
              <Styles.ProductIcon iconColor={iconColor} data-testid="app-icon">
                {iconText || ''}
              </Styles.ProductIcon>
            )}
            <Styles.CardTitle>
              {name}
              {isRestricted && (
                <RestrictedTooltip message={restrictedTooltipMsg}></RestrictedTooltip>
              )}

              {!isEnabled && isDriveApp && !warningMessage?.trim() && (
                <Styles.WarningMessage>
                  <Styles.WarningIcon />{t('baseCard.driveWarningMsg')}
                </Styles.WarningMessage>
              )}
              {shouldShowWarning(warningMessage, requiredOS) && (
                <Styles.WarningMessage>
                  <Styles.WarningIcon />{warningMessage}
                </Styles.WarningMessage>
              )}
              {isMobileApp && (
                <Styles.WarningMessage>
                  <Styles.WarningIcon data-testid="mobile-app-warning" />
                  {t('baseCard.mobileAppWarning')}
                </Styles.WarningMessage>
              )}
            </Styles.CardTitle>
          </Styles.CardHeader>
          {showDescription && <Styles.CardDescription>{description}</Styles.CardDescription>}
        </div>
        <div style={{ display: 'flex', top: '5px', right: '5px', position: 'absolute', width: 'auto' }}>
          {isEnabled && !isRestricted && enableAdoptease && adoptEaseISFlag && !isApplicationopened &&
            <Styles.TryNowButton
              tabIndex={0}
              onClick={tryNowClick}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  tryNowClick(e);
                }
              }}
              data-testid="try-now">
              <div style={{ height: '20px', display: 'flex' }}>
                <svg fill="#0B5FFF" width="23px" height="23px" viewBox="0 0 512 512">
                  <g id="Graduation">
                    <polygon points="445.055 384.794 445.055 221.864 418.805 234.989 418.805 384.777 401.301 429.785 462.551 429.785 445.055 384.794" />
                    <path d="M229.0648,306.3708l-107.7643-53.88v53.7754c0,36.2433,58.7634,65.625,131.25,65.625,72.4887,0,131.25-29.3817,131.25-65.625V252.49L276.0277,306.3741C257.5813,313.681,247.5133,313.6789,229.0648,306.3708Z" />
                    <path d="M264.2912,282.8969l186.5207-93.26c6.4579-3.2289,6.4579-8.5107,0-11.74l-186.5207-93.26c-6.4556-3.2289-17.0214-3.2289-23.4793,0l-186.5207,93.26c-6.4556,3.2289-6.4556,8.5107,0,11.74l186.5207,93.26C247.27,286.1258,257.8356,286.1258,264.2912,282.8969Z" />
                  </g>
                </svg>
                <Styles.TryNowText style={{ color: "#0B5FFF" }}>
                  {t('adoptease.tryNow')}
                </Styles.TryNowText>
              </div>
            </Styles.TryNowButton>
          }

          {showNewIndicator && <Styles.StatusIndicatorIcon style={{ right: (userLocale.locale === 'en_FR') ? '37px' : '7px' }}>
            <svg width="13" height="16" xmlns="http://www.w3.org/2000/svg">
              &nbsp; <rect width="10" height="10" x="0" y="3" rx="2" ry="2" fill="#09AD00"></rect>
              Sorry, your browser does not support inline SVG.
            </svg><Styles.StatusIndicatorText>{t('baseCard.new')}</Styles.StatusIndicatorText>
          </Styles.StatusIndicatorIcon>}
        </div>

        <Styles.MenuButton ref={moreRef}
          dataTestId='icon-card-more'
          data-id='icon-card-more'
          icon={<IconMoreVert />}
          onClick={onMoreClick}
          isDisabled={isRestricted}
          text=""
          tabIndex={tabIndex}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter') {
              clickByMenu = true;
              onMoreClick(e);
            }
          }}
        />
        <Styles.StyledMenuContainer labelRef={moreRef} dataTestId="bc-menu-more">
          {menuOptions.map((option, ind) => (
            <Styles.StyledMenuListItem
              key={ind}
              isSelected={false}
              dataTestId={`lm-mn-${ind}`}
              graphicContent={option.icon}
              onClick={(e: MouseEvent) => { clickByMenu = true; onMenuClick(option); }}
            >
              {option.label}
            </Styles.StyledMenuListItem>
          ))}
        </Styles.StyledMenuContainer>
      </Styles.Card>
      {showAdopteaseDialogue && <AdoptEaseApplicationDialog
        isOpen={true}
        onClose={() => setShowAdopteaseDialogue(false)}
        iconColor={iconColor}
        iconText={iconText}
        name={name}
        description={description}
        cdkUUrl={cdkuUrl ? cdkuUrl : ''}
        id={id ? id : ''}
      />
      }

    </>
  );
}
